// 16-bit greyscale support for the annotation viewer.
//
// CVAT renders every frame through an 8-bit canvas: the server converts the upload to JPEG and
// the Min/Max sliders are an SVG feComponentTransfer applied on top of that. For fluorescence
// microscopy that throws the measurement away before the annotator sees it -- our frames carry
// 257-684 distinct values between the 1st percentile and the maximum, and the windowing the
// biologists actually use (Min 123 / Max 195 on a 14-bit camera) cannot be expressed once the
// data has been squeezed into 256 codes with a fixed curve baked in at upload time.
//
// So: fetch the ORIGINAL frame (a 16-bit greyscale PNG), decode it here into a Uint16Array and
// do the windowing at full precision. The canvas stays 8-bit -- that is what a browser gives you
// -- but the 8 bits it receives are now chosen by the annotator from the real 16-bit data.
//
// Everything degrades to a no-op if the original is not a 16-bit greyscale PNG, so ordinary
// 8-bit tasks, and everybody else on the server, are untouched.

import Axios from 'axios';
import { inflate } from 'pako';

// `Gray16` is an interface: it must travel as a TYPE import/export or Babel strips it during
// transpilation and webpack then fails looking for a runtime binding that never existed.
import type { Gray16 } from './sixteen-bit-decode';
import { decodeGray16PNG } from './sixteen-bit-decode';

export type { Gray16 } from './sixteen-bit-decode';
export { decodeGray16PNG } from './sixteen-bit-decode';

// A decoded 1280x1280 frame is 3.3 MB and the annotators scrub through hundreds of them, so the
// cache is bounded: an unbounded Map would grow to gigabytes over a long job. Insertion order is
// the eviction order (Map preserves it), and a hit re-inserts so the entry moves back to the end.
const CACHE_LIMIT = 12;
const cache = new Map<string, Gray16 | null>();
const pending = new Map<string, Promise<Gray16 | null>>();

// Whether a job is 16-bit can only be learned by fetching an original and looking at it, and on an
// ordinary task that download is pure waste -- doubling the bytes pulled for every frame the
// annotator steps through. So it is probed ONCE per job: the first frame that comes back as
// something other than a 16-bit greyscale PNG rules the whole job out.
//
// The cost is that a task deliberately MIXING 16-bit PNGs with other images would only get the
// 16-bit treatment if its first-viewed frame happened to be one, which is a trade worth making
// against an extra full-resolution download per frame for everybody else.
const ruledOut = new Set<number>();

function remember(key: string, value: Gray16 | null): void {
    cache.delete(key);
    cache.set(key, value);
    while (cache.size > CACHE_LIMIT) {
        const oldest = cache.keys().next();
        if (oldest.done) break;
        cache.delete(oldest.value);
    }
}

export async function loadGray16(jobID: number, frame: number): Promise<Gray16 | null> {
    if (ruledOut.has(jobID)) return null;
    const key = `${jobID}:${frame}`;
    if (cache.has(key)) {
        const hit = cache.get(key) as Gray16 | null;
        remember(key, hit);
        return hit;
    }
    const running = pending.get(key);
    if (running) return running;

    const request = (async (): Promise<Gray16 | null> => {
        try {
            const response = await Axios.get(`/api/jobs/${jobID}/data`, {
                params: { quality: 'original', type: 'frame', number: frame },
                responseType: 'arraybuffer',
            });
            const decoded = decodeGray16PNG(response.data, inflate);
            // The server answered and the answer was not a 16-bit greyscale PNG: that is a fact
            // about the job, so stop probing it.
            if (!decoded) ruledOut.add(jobID);
            remember(key, decoded);
            return decoded;
        } catch (error) {
            // A FAILED request is not the same fact. Ruling the job out here meant one 5xx from a
            // busy worker, or a dropped connection, silently downgraded the whole task to the
            // 8-bit path for the rest of the session with no way back short of a reload. Only a
            // 4xx -- originals genuinely not there or not ours -- says anything about the job.
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (typeof status === 'number' && status >= 400 && status < 500) {
                ruledOut.add(jobID);
            }
            // Either way this frame is cached as not-16-bit, so a repaint cannot start a retry
            // storm; stepping away and back is what gets it re-fetched.
            remember(key, null);
            return null;
        } finally {
            pending.delete(key);
        }
    })();
    pending.set(key, request);
    return request;
}

/**
 * The display curve applied across the Min/Max window. Linear is ImageJ's; gamma and asinh are
 * global, monotonic alternatives for frames whose bright cell body and faint filopodia do not fit
 * one linear window -- unlike CLAHE they treat every pixel of the frame the same way and never
 * reorder intensities, so an outline drawn on them follows the real signal.
 *
 * `parameter` is the gamma exponent (below 1 lifts the darks) or the asinh softness (smaller is a
 * stronger log-like compression; asinh is the server-side JPEG preview's curve).
 */
export type DisplayCurve = 'linear' | 'gamma' | 'asinh';

export const DEFAULT_GAMMA = 1;
export const DEFAULT_ASINH_SOFTNESS = 0.1;

/** 0..1 within the window -> 0..1 on screen. */
export function applyCurve(t: number, curve: DisplayCurve, parameter: number): number {
    const x = Math.min(Math.max(t, 0), 1);
    if (curve === 'gamma') return x ** parameter;
    if (curve === 'asinh') return Math.asinh(x / parameter) / Math.asinh(1 / parameter);
    return x;
}

/**
 * The 256-entry curve over an 8-bit input, for the SVG filter on ordinary (non-16-bit) frames:
 * entry k is what input grey k shows as, in 0..1.
 */
export function curveTable(min: number, max: number, curve: DisplayCurve, parameter: number): number[] {
    const span = Math.max(max - min, 1);
    const table: number[] = [];
    for (let k = 0; k < 256; k++) {
        table.push(applyCurve((k - min) / span, curve, parameter));
    }
    return table;
}

/**
 * Window 16-bit data into 8-bit RGBA. min/max are in SOURCE units, so on a 14-bit camera the
 * sliders address 0..16383 rather than 0..255. Built through a lookup table rather than a
 * per-pixel divide: at 1280x1280 this runs over 1.6M pixels on every slider tick.
 */
export function windowToImageData(
    source: Gray16, min: number, max: number, curve: DisplayCurve = 'linear', parameter = 1,
): ImageData {
    const lut = new Uint8ClampedArray(65536);
    if (curve === 'linear') {
        // ImageJ's own mapping (ShortProcessor.create8BitImage, with the display range rounded to
        // integers as ShortProcessor.setMinAndMax does), so the same Min/Max gives the same grey
        // levels as Fiji -- checked pixel for pixel against ij.jar. A plain (v - min) / (max - min)
        // * 255 put ~8 % of pixels one or two levels off.
        const low = Math.round(min);
        const scale = 256 / (Math.max(Math.round(max) - low, 0) + 1);
        for (let value = 0; value < 65536; value++) {
            lut[value] = Math.floor(Math.max(value - low, 0) * scale + 0.5);
        }
    } else {
        // Unrounded min/max here: the asinh Auto reproduces the server's preview, whose black
        // point is an interpolated percentile.
        const span = Math.max(max - min, 1e-9);
        for (let value = 0; value < 65536; value++) {
            lut[value] = Math.round(applyCurve((value - min) / span, curve, parameter) * 255);
        }
    }
    const { width, height, data } = source;
    const out = new ImageData(width, height);
    const rgba = out.data;
    for (let i = 0, j = 0; i < data.length; i++, j += 4) {
        const grey = lut[data[i]];
        rgba[j] = grey;
        rgba[j + 1] = grey;
        rgba[j + 2] = grey;
        rgba[j + 3] = 255;
    }
    return out;
}

// ImageJ's Auto threshold (ContrastAdjuster.AUTO_THRESHOLD). ImageJ halves it on each further
// click; CVAT deliberately does not -- Auto is a single state, the result of the first click.
export const AUTO_THRESHOLD = 5000;

/** ImageJ's Reset: the frame's full data range, nothing clipped at either end. */
export function fullRange(source: Gray16): { min: number; max: number } {
    const { data } = source;
    let min = 65535;
    let max = 0;
    for (let i = 0; i < data.length; i++) {
        const value = data[i];
        if (value < min) min = value;
        if (value > max) max = value;
    }
    if (max <= min) return { min: 0, max: Math.max(max, 1) };
    return { min, max };
}

/**
 * ImageJ/Fiji's Brightness & Contrast "Auto", ported from ContrastAdjuster.autoAdjust so that the
 * same frame gets the same window here as in Fiji. It is NOT a percentile stretch -- the previous
 * 0.1/99.9 percentile version put Max 1.5-3x higher than Fiji on our fluorescence frames, because
 * a cumulative percentile counts the long, sparse bright tail that ImageJ's per-bin test ignores:
 *
 *   - 256 bins spanning the frame's own min..max (ImageJ's 16-bit statistics histogram);
 *   - a bin holding more than 10 % of all pixels is ignored (the background peak);
 *   - Min is the start of the first bin, Max the start of the last bin, holding more than
 *     pixelCount / autoThreshold pixels.
 *
 * Computed from the samples rather than from the canvas: the canvas holds the ALREADY windowed
 * 8-bit result, and measuring that would hand back a 0..255 window for a 0..2020 scale.
 */
export function autoWindow(source: Gray16, autoThreshold = AUTO_THRESHOLD): { min: number; max: number } {
    const { data } = source;
    const full = fullRange(source);
    if (full.max <= full.min) return full;

    const bins = 256;
    const histogram = new Uint32Array(bins);
    const scale = bins / (full.max - full.min);
    for (let i = 0; i < data.length; i++) {
        const index = Math.floor((data[i] - full.min) * scale);
        histogram[index >= bins ? bins - 1 : index] += 1;
    }

    const limit = Math.floor(data.length / 10);
    const threshold = Math.floor(data.length / autoThreshold);
    const counts = (index: number): number => (histogram[index] > limit ? 0 : histogram[index]);
    let hmin = 0;
    while (hmin < bins - 1 && counts(hmin) <= threshold) hmin++;
    let hmax = bins - 1;
    while (hmax > 0 && counts(hmax) <= threshold) hmax--;

    // ImageJ resets to the full range when no bin qualifies or the window collapses.
    if (hmax < hmin) return full;
    const binSize = (full.max - full.min) / bins;
    const min = Math.round(full.min + hmin * binSize);
    const max = Math.round(full.min + hmax * binSize);
    return max > min ? { min, max } : full;
}

/**
 * ImageJ's Process > Enhance Contrast with "saturated=0.35" (ContrastEnhancer.stretchHistogram on a
 * single 16-bit image, no normalize), which is what the Auto button uses. Gentler than
 * autoWindow above on our fluorescence frames: autoWindow ignores the sparse bright tail and so
 * burns the whole cell body white, while this lets exactly `saturated` % of the pixels clip, half
 * at each end, counted over the full 65536-value histogram.
 */
export function enhanceContrast(source: Gray16, saturated = 0.35): { min: number; max: number } {
    const histogram = new Uint32Array(65536);
    const { data } = source;
    for (let i = 0; i < data.length; i++) {
        histogram[data[i]] += 1;
    }
    const threshold = saturated > 0 ? Math.floor((data.length * saturated) / 200) : 0;
    let count = 0;
    let min = -1;
    do {
        min++;
        count += histogram[min];
    } while (count <= threshold && min < 65535);
    count = 0;
    let max = 65536;
    do {
        max--;
        count += histogram[max];
    } while (count <= threshold && max > 0);
    // ImageJ leaves the display range untouched when the window collapses.
    return max > min ? { min, max } : fullRange(source);
}

// Where the curve Autos put the background: the median lands on this grey. The server's asinh
// JPEG preview uses the same target (TARGET_BACKGROUND in media_extractors.py).
const CURVE_TARGET_BACKGROUND = 30 / 255;

/**
 * numpy.percentile (default, linear interpolation) of the samples, through a histogram instead of
 * a sort: q in 0..100.
 */
function percentileOf(histogram: Uint32Array, total: number, q: number): number {
    const position = ((total - 1) * q) / 100;
    const lowRank = Math.floor(position);
    const fraction = position - lowRank;
    let seen = 0;
    let lowValue = -1;
    for (let value = 0; value < histogram.length; value++) {
        seen += histogram[value];
        if (lowValue < 0 && seen > lowRank) lowValue = value;
        if (seen > lowRank + 1 || (fraction === 0 && lowValue >= 0)) {
            return fraction === 0 ? lowValue : lowValue + fraction * (value - lowValue);
        }
    }
    return lowValue;
}

/**
 * Auto for the gamma and asinh curves, following the server's asinh preview so the two agree:
 * black at the 1st percentile, white at the TRUE maximum (nothing clips -- the curve, not the
 * window, is what keeps the cell body from burning out), and the curve parameter solved so the
 * background median lands on CURVE_TARGET_BACKGROUND.
 */
export function autoCurve(source: Gray16, curve: 'gamma' | 'asinh'): {
    min: number; max: number; parameter: number;
} {
    const { data } = source;
    const histogram = new Uint32Array(65536);
    let top = 0;
    for (let i = 0; i < data.length; i++) {
        const value = data[i];
        histogram[value] += 1;
        if (value > top) top = value;
    }
    const black = percentileOf(histogram, data.length, 1);
    const white = top;
    const background = Math.min(Math.max(
        (percentileOf(histogram, data.length, 50) - black) / (white - black + 1e-9), 0,
    ), 1);
    if (white <= black || background <= 0 || background >= 1) {
        return {
            min: black,
            max: Math.max(white, black + 1),
            parameter: curve === 'gamma' ? DEFAULT_GAMMA : DEFAULT_ASINH_SOFTNESS,
        };
    }
    if (curve === 'gamma') {
        // background ** gamma = target
        const gamma = Math.log(CURVE_TARGET_BACKGROUND) / Math.log(background);
        return { min: black, max: white, parameter: Math.min(Math.max(gamma, 0.05), 5) };
    }
    // The server's bisection, step for step: asinh(b/a) / asinh(1/a) falls as a grows.
    let lo = 1e-4;
    let hi = 1e4;
    for (let step = 0; step < 64; step++) {
        const mid = Math.sqrt(lo * hi);
        if (Math.asinh(background / mid) / Math.asinh(1 / mid) > CURVE_TARGET_BACKGROUND) {
            lo = mid;
        } else {
            hi = mid;
        }
    }
    return { min: black, max: white, parameter: Math.sqrt(lo * hi) };
}

/** The Auto button for whichever curve is selected. */
export function autoDisplay(source: Gray16, curve: DisplayCurve, parameter: number): {
    min: number; max: number; parameter: number;
} {
    if (curve === 'linear') return { ...enhanceContrast(source), parameter };
    return autoCurve(source, curve);
}

let lastLoaded: Gray16 | null = null;

/**
 * The slider range for a frame whose brightest sample is `max`, in SOURCE units: 0..4095 on a
 * 12-bit sensor, not 0..255. Rounded up to the sensor's bit depth rather than used raw, so the
 * sliders keep the same scale as the annotator steps between frames -- a range that silently
 * rescaled itself per frame would make a fixed window, which is the whole point of the exercise,
 * impossible to hold.
 *
 * Pure, and the result goes into redux via changeLevelLimit rather than being kept here: the
 * settings panel renders the sliders from it, and React does not re-render on a module variable
 * changing. Held as module state it went stale the moment the annotator stepped onto a frame of
 * a different depth with the panel already open.
 */
export function levelLimitFor(source: Gray16 | null): number {
    if (!source) return 255;
    const { max } = source;
    if (max <= 255) return 255;
    if (max <= 1023) return 1023;
    if (max <= 4095) return 4095;
    if (max <= 16383) return 16383;
    return 65535;
}

/** The most recently displayed 16-bit frame, or null on an ordinary (8-bit) frame. */
export function currentGray16(): Gray16 | null {
    return lastLoaded;
}

/**
 * Records the frame now on screen. Stays module state deliberately, unlike the level limit: it is
 * only ever read imperatively -- by the SVG filter update and by the Auto/Reset click handlers --
 * so nothing renders from it and there is nothing to go stale.
 */
export function noteFrame(source: Gray16 | null): void {
    lastLoaded = source;
}
